// Theme configuration with Light and Dark variants
// Healthcare-grade color palette: Teal primary, Blue accent
export const themes = {
  dark: {
    // Base colors
    background: {
      primary: '#0E0E0F',           // Main background
      secondary: 'rgba(26, 26, 28, 0.85)', // Header background
      tertiary: 'rgba(26, 26, 28, 0.6)',   // Messages container
      surface: 'rgba(35, 35, 38, 0.96)',    // Agent message bubble
      surfaceHover: 'rgba(40, 40, 43, 0.95)', // Agent message hover
      overlay: 'rgba(0, 0, 0, 0.5)',        // Loading overlay
    },
    
    // Text colors
    text: {
      primary: '#E8E9ED',           // Main text
      secondary: '#D1D1D6',         // Muted text
      white: '#F5F5F7',             // Logo title, emphasis
      placeholder: '#666',          // Input placeholder
    },
    
    // Brand colors — Healthcare Teal + Blue
    brand: {
      purple: '#0D9488',            // Primary teal (key name kept for compat)
      purpleDark: '#0F766E',        // Darker teal
      purpleLight: '#2DD4BF',       // Lighter teal
      pink: '#2563EB',              // Blue accent (was pink)
      blue: '#58A6FF',              // Blue accent
      blueLight: '#61dafb',         // Light blue (links)
      error: '#FF8A80',             // Error state
      errorBg: 'rgba(255, 88, 88, 0.12)', // Error background
    },
    
    // Gradients — Teal/Blue healthcare gradients
    gradient: {
      logo: 'linear-gradient(135deg, #0D9488 0%, #58A6FF 50%, #2563EB 100%)',
      logoWrapper: 'linear-gradient(135deg, rgba(13, 148, 136, 0.15) 0%, rgba(88, 166, 255, 0.15) 100%)',
      userMessage: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
      welcomeTitle: 'linear-gradient(135deg, #0D9488 0%, #2563EB 100%)',
      messagesTop: 'linear-gradient(180deg, rgba(26, 26, 28, 0.3) 0%, rgba(26, 26, 28, 0.5) 100%)',
      aurora1: 'rgba(13, 148, 136, 0.03)',  // Teal aurora
      aurora2: 'rgba(37, 99, 235, 0.03)',   // Blue aurora
    },
    
    // Borders
    border: {
      default: 'rgba(255, 255, 255, 0.08)',
      subtle: 'rgba(255, 255, 255, 0.05)',
      input: 'rgba(255, 255, 255, 0.1)',
      logo: 'rgba(13, 148, 136, 0.35)',
      logoHover: 'rgba(13, 148, 136, 0.65)',
      userMessage: 'rgba(255, 255, 255, 0.1)',
      agentAccent: 'rgba(88, 166, 255, 0.5)',
      agentAccentHover: 'rgba(88, 166, 255, 0.8)',
      image: 'rgba(255, 255, 255, 0.1)',
    },
    
    // Shadows
    shadow: {
      header: '0 1px 0 rgba(255, 255, 255, 0.05) inset, 0 4px 24px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.2)',
      logo: '0 0 26px rgba(13, 148, 136, 0.25), 0 4px 14px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
      logoHover: '0 0 34px rgba(13, 148, 136, 0.5), 0 0 70px rgba(13, 148, 136, 0.25), 0 10px 24px rgba(0, 0, 0, 0.45)',
      logoFilter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.35))',
      logoFilterHover: 'drop-shadow(0 4px 16px rgba(13, 148, 136, 0.6))',
      titleIcon: 'drop-shadow(0 2px 8px rgba(13, 148, 136, 0.4))',
      titleIconHover: 'drop-shadow(0 4px 16px rgba(13, 148, 136, 0.8))',
      titleText: '0 2px 8px rgba(0, 0, 0, 0.3)',
      messagesContainer: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      userMessage: '0 4px 16px rgba(13, 148, 136, 0.25), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
      userMessageHover: '0 6px 24px rgba(13, 148, 136, 0.35), 0 4px 12px rgba(0, 0, 0, 0.4)',
      agentMessage: '0 4px 16px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      agentMessageHover: '0 6px 24px rgba(0, 0, 0, 0.4), 0 0 20px rgba(88, 166, 255, 0.1)',
      messageBubble: '0 1px 1px rgba(0, 0, 0, 0.35)',
      image: '0 4px 12px rgba(0, 0, 0, 0.3)',
      imageHover: '0 8px 24px rgba(0, 0, 0, 0.4)',
      error: '0 0 20px rgba(255, 88, 88, 0.15), 0 4px 12px rgba(0, 0, 0, 0.3)',
      focus: '0 0 0 4px rgba(45, 212, 191, 0.25)',
    },
    
    // Special effects
    effects: {
      backdropBlur: '40px',
      logoGlowColor: '#0D9488',
      errorGlow: 'rgba(255, 88, 88, 0.15)',
    },
    
    // Input bar specific colors
    inputBar: {
      background: 'rgba(35, 35, 38, 0.6)',
      backgroundFocus: 'rgba(35, 35, 38, 0.8)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderFocus: 'rgba(13, 148, 136, 0.4)',
      shadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      shadowFocus: '0 0 0 3px rgba(13, 148, 136, 0.15), 0 8px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      gradientOverlay: 'linear-gradient(135deg, rgba(13, 148, 136, 0.3) 0%, rgba(37, 99, 235, 0.3) 100%)',
    },
    
    // Input wrapper (container) colors
    inputWrapper: {
      shadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    },
    
    // Logo images
    logo: {
      image: '/logo.png',  // Logo from public folder
    },
    
    // Auth page colors (Login, Signup, ForgotPassword & components)
    auth: {
      containerBg: '#111827',
      containerText: '#e5e7eb',
      containerBorder: '#e5e5e5',
      containerShadow: '0 10px 30px rgba(0,0,0,0.4)',
      labelColor: '#e5e7eb',
      inputBorder: '#ccc',
      inputBorderError: '#e53935',
      inputText: '#e5e7eb',
      inputBg: 'rgba(255, 255, 255, 0.05)',
      buttonPrimary: '#0D9488',
      buttonPrimaryHover: '#0F766E',
      buttonDisabled: '#ccc',
      buttonText: '#fff',
      buttonSecondary: '#0D9488',
      buttonSecondaryDisabled: '#888',
      errorBg: '#fdecea',
      errorBorder: '#f5c6cb',
      errorText: '#b71c1c',
      mutedText: '#666',
      spinnerColor: '#60a5fa',
      spinnerTrack: 'rgba(255, 255, 255, 0.2)',
    },
  },
  
  light: {
    // Base colors
    background: {
      primary: '#FFFFFF',           // Main background
      secondary: 'rgba(248, 248, 250, 0.95)', // Header background
      tertiary: 'rgba(245, 245, 247, 0.8)',   // Messages container
      surface: 'rgba(255, 255, 255, 0.98)',   // Agent message bubble
      surfaceHover: 'rgba(245, 245, 247, 1)',  // Agent message hover
      overlay: 'rgba(0, 0, 0, 0.3)',         // Loading overlay
    },
    
    // Text colors
    text: {
      primary: '#1F1F23',           // Main text
      secondary: '#6B6B70',         // Muted text
      white: '#000000',             // Logo title (will be dark in light mode)
      placeholder: '#999',          // Input placeholder
    },
    
    // Brand colors — Healthcare Teal + Blue
    brand: {
      purple: '#0F766E',            // Primary teal (slightly darker for light mode)
      purpleDark: '#115E59',        // Darker teal
      purpleLight: '#0D9488',       // Lighter teal
      pink: '#2563EB',              // Blue accent (was pink)
      blue: '#2563EB',              // Blue accent
      blueLight: '#3B82F6',         // Light blue (links)
      error: '#DC2626',             // Error state
      errorBg: 'rgba(220, 38, 38, 0.1)', // Error background
    },
    
    // Gradients — Teal/Blue healthcare gradients
    gradient: {
      logo: 'linear-gradient(135deg, #0F766E 0%, #2563EB 50%, #0D9488 100%)',
      logoWrapper: 'linear-gradient(135deg, rgba(15, 118, 110, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
      userMessage: 'linear-gradient(135deg, #0F766E 0%, #115E59 100%)',
      welcomeTitle: 'linear-gradient(135deg, #0F766E 0%, #2563EB 100%)',
      messagesTop: 'linear-gradient(180deg, rgba(245, 245, 247, 0.5) 0%, rgba(245, 245, 247, 0.8) 100%)',
      aurora1: 'rgba(15, 118, 110, 0.05)',  // Teal aurora
      aurora2: 'rgba(37, 99, 235, 0.05)',   // Blue aurora
    },
    
    // Borders
    border: {
      default: 'rgba(0, 0, 0, 0.1)',
      subtle: 'rgba(0, 0, 0, 0.05)',
      input: 'rgba(0, 0, 0, 0.15)',
      logo: 'rgba(15, 118, 110, 0.3)',
      logoHover: 'rgba(15, 118, 110, 0.6)',
      userMessage: 'rgba(0, 0, 0, 0.05)',
      agentAccent: 'rgba(37, 99, 235, 0.4)',
      agentAccentHover: 'rgba(37, 99, 235, 0.7)',
      image: 'rgba(0, 0, 0, 0.1)',
    },
    
    // Shadows
    shadow: {
      header: '0 1px 0 rgba(255, 255, 255, 0.8) inset, 0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)',
      logo: '0 0 20px rgba(15, 118, 110, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
      logoHover: '0 0 24px rgba(15, 118, 110, 0.3), 0 0 50px rgba(15, 118, 110, 0.15), 0 6px 16px rgba(0, 0, 0, 0.15)',
      logoFilter: 'drop-shadow(0 1px 4px rgba(0, 0, 0, 0.15))',
      logoFilterHover: 'drop-shadow(0 2px 8px rgba(15, 118, 110, 0.4))',
      titleIcon: 'drop-shadow(0 1px 4px rgba(15, 118, 110, 0.3))',
      titleIconHover: 'drop-shadow(0 2px 8px rgba(15, 118, 110, 0.6))',
      titleText: '0 1px 4px rgba(0, 0, 0, 0.1)',
      messagesContainer: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
      userMessage: '0 2px 8px rgba(15, 118, 110, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
      userMessageHover: '0 4px 16px rgba(15, 118, 110, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15)',
      agentMessage: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
      agentMessageHover: '0 4px 16px rgba(0, 0, 0, 0.12), 0 0 12px rgba(37, 99, 235, 0.08)',
      messageBubble: '0 1px 1px rgba(0, 0, 0, 0.1)',
      image: '0 2px 8px rgba(0, 0, 0, 0.1)',
      imageHover: '0 4px 16px rgba(0, 0, 0, 0.15)',
      error: '0 0 12px rgba(220, 38, 38, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
      focus: '0 0 0 4px rgba(15, 118, 110, 0.2)',
    },
    
    // Special effects
    effects: {
      backdropBlur: '20px',
      logoGlowColor: '#0F766E',
      errorGlow: 'rgba(220, 38, 38, 0.1)',
    },
    
    // Input bar specific colors - Elevated and visible for light mode
    inputBar: {
      background: '#FFFFFF',
      backgroundFocus: '#FFFFFF',
      border: 'rgba(0, 0, 0, 0.12)',
      borderFocus: 'rgba(15, 118, 110, 0.5)',
      shadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
      shadowFocus: '0 0 0 3px rgba(15, 118, 110, 0.12), 0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      gradientOverlay: 'linear-gradient(135deg, rgba(15, 118, 110, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
    },
    
    // Input wrapper (container) colors - Subtle elevation for light mode
    inputWrapper: {
      shadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
    },
    
    // Logo images
    logo: {
      image: '/logo.png',  // Logo from public folder
    },
    
    // Auth page colors (Login, Signup, ForgotPassword & components)
    auth: {
      containerBg: '#FAF9F7',
      containerText: '#2D2B30',
      containerBorder: 'rgba(0, 0, 0, 0.09)',
      containerShadow: '0 4px 20px rgba(0,0,0,0.06)',
      labelColor: '#2D2B30',
      inputBorder: 'rgba(0, 0, 0, 0.13)',
      inputBorderError: '#C0392B',
      inputText: '#2D2B30',
      inputBg: '#F7F5F2',
      buttonPrimary: '#0F766E',
      buttonPrimaryHover: '#115E59',
      buttonDisabled: '#d0cdc8',
      buttonText: '#fff',
      buttonSecondary: '#0F766E',
      buttonSecondaryDisabled: '#a8a5a0',
      errorBg: '#FDF0EE',
      errorBorder: '#F0C4BD',
      errorText: '#8B3A2F',
      mutedText: '#7A7680',
      spinnerColor: '#0D9488',
      spinnerTrack: 'rgba(0, 0, 0, 0.08)',
    },
  },
};

// Helper function to get current theme
export const getTheme = (mode = 'dark') => themes[mode] || themes.dark;
